import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { AppRole } from './navigation.service';

// ── Reactive app state (used by layout components + services) ──────────────

export type AppTheme = 'night' | 'day';

export interface AppState {
  role: AppRole;
  theme: AppTheme;
  notifications: number;
  spinnerCount: number;
  sidebarCollapsed: boolean;
}

const INITIAL_STATE: AppState = {
  role: 'user',
  theme: 'night',
  notifications: 4,
  spinnerCount: 0,
  sidebarCollapsed: false,
};

// ─────────────────────────────────────────────────────────────────────────────
// GetterSetterService
//
// Two complementary APIs in one service:
//
//   1. Reactive state store (BehaviorSubject) — drives sidebar, topnav,
//      theme toggle and role switcher via Observable streams.
//
//   2. Local-storage helpers — persist user session data (details, tokens,
//      dropdown caches) across page refreshes.
// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class GetterSetterService {

  // ── Reactive store ─────────────────────────────────────────────────────────

  private readonly state$ = new BehaviorSubject<AppState>(INITIAL_STATE);

  /** Project a single slice of state as an Observable. */
  select<K extends keyof AppState>(key: K): Observable<AppState[K]> {
    return this.state$.pipe(
      map((s) => s[key]),
      distinctUntilChanged(),
    );
  }

  /** Snapshot read — prefer `select` in templates and reactive chains. */
  get<K extends keyof AppState>(key: K): AppState[K] {
    return this.state$.value[key];
  }

  /** Patch a single state key. No-ops when the value is unchanged. */
  set<K extends keyof AppState>(key: K, value: AppState[K]): void {
    const current = this.state$.value;
    if (current[key] === value) return;
    this.state$.next({ ...current, [key]: value });
  }

  /** Reset the entire store to its initial state. */
  reset(): void {
    this.state$.next(INITIAL_STATE);
  }

  // ── Local-storage helpers ─────────────────────────────────────────────────
  setLoggedInUser(data: any): void {
    localStorage.setItem('loggedUserDetails', JSON.stringify(data));
  }
  getLoggedInUser(): any {
    return this.parseItem('loggedUserDetails');
  }


  setUserDetails(data: any): void {
    if (data === undefined || data === null) {
      localStorage.removeItem('userDetails');
      return;
    }
    localStorage.setItem('userDetails', JSON.stringify(data));
  }

  getUserDetails(): any {
    return this.parseItem('userDetails');
  }

  getItem(key: string): any {
    return this.parseItem(key);
  }

  setOtpToken(otpToken: any): void {
    localStorage.setItem('otptoken', otpToken);
  }

  getOtpToken(): string {
    return localStorage.getItem('otptoken') ?? '';
  }

  setToken(token: any): void {
    localStorage.setItem('token', String(token));
  }

  getToken(): string {
    return localStorage.getItem('token') ?? '';
  }

  /** Alias kept for backward compatibility with code that calls verifyOtp flow. */
  setVerifyOtpToken(token: any): void {
    this.setToken(token);
  }

  getVerifyOtpToken(): string {
    return this.getToken();
  }

  /** Clears user session from localStorage. Does NOT reset reactive state. */
  logout(): void {
    localStorage.removeItem('userDetails');
    localStorage.removeItem('token');
    localStorage.removeItem('otptoken');
    localStorage.removeItem('loggedUserDetails');
    localStorage.removeItem('dropdownData');
    localStorage.removeItem('theme');
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private parseItem(key: string): any {
    const raw = localStorage.getItem(key);
    if (raw == null || raw === '' || raw === 'undefined' || raw === 'null') {
      return {};
    }
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
}
